import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { getApiCall, postApiCall } from 'api/apiService';
import {
  GET_SOLICITOR_PDF,
  GET_VENDOR_PDF,
  SOLICITOR_CONFIRM_SIGNATURE,
  UPLOAD_VENDOR_SIGN,
  VENDOR_CONFIRM_SIGNATURE,
} from 'constant/app.config';
import { API_URL } from 'constant/app.config';
import CustomSignaturePadDialog from './Common/CustomSignaturePanel';
import { poppins400, poppins500 } from 'theme/common.style';
import { useTheme } from '@emotion/react';
import Swal from 'sweetalert2';
import Loading from 'assets/images/loading.png';
import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import CommonPdfViewer from './Common/CommonPdfViewer';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

const GeneratePdfDialog = ({
  open,
  onClose,
  propertyId,
  buyerId,
  address,
  status,
  offerUniqueId,
  getListInfo,
  confirmLoading,
  setConfirmLoading,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const matches450 = useMediaQuery(theme.breakpoints.down(450));

  const [pdfPath, setPdfPath] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('review');
  const [openSignPad, setOpenSignPad] = useState(false);
  const [noDocumentAvailable, setNoDocumentAvailable] = useState(false);
  const [pdfRendering, setPdfRendering] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const userDetail = useMemo(
    () => JSON.parse(localStorage.getItem('userDetail') || '{}'),
    []
  );
  const vendorId = userDetail?.userId;
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    try {
      const sessionSign = sessionStorage.getItem('buyerSignature');
      if (sessionSign) {
        const parsed = JSON.parse(sessionSign);
        if (parsed.userId !== userDetail?.userId) {
          sessionStorage.removeItem('buyerSignature');
          sessionStorage.removeItem('signUrl');
        }
      }
    } catch (e) {}
  }, [userDetail?.userId]);

  useEffect(() => {
    const localSignature = !!userDetail?.signUrl;

    let sessionSignature = false;
    try {
      const sessionSign = sessionStorage.getItem('buyerSignature');
      if (sessionSign) {
        const parsed = JSON.parse(sessionSign);
        if (parsed.userId === userDetail?.userId) {
          sessionSignature = true;
        }
      }
    } catch (e) {}

    setHasSignature(localSignature || sessionSignature);
  }, [userDetail?.userId, userDetail?.signUrl, open]);

  const getSignatureFromStorage = () => {
    try {
      const sessionSign = sessionStorage.getItem('buyerSignature');
      if (sessionSign) {
        const parsed = JSON.parse(sessionSign);
        if (parsed.userId === userDetail?.userId) {
          return parsed.base64;
        }
      }
    } catch (e) {}

    return userDetail?.signUrl || null;
  };

  const isDialogOpenRef = useRef(false);

  useEffect(() => {
    isDialogOpenRef.current = open;
    return () => {
      isDialogOpenRef.current = false;
    };
  }, [open]);

  useEffect(() => {
    if (open && buyerId !== undefined) {
      setStep('review');
      setPdfLoaded(false);
      setPdfLoadError(false);
      setPdfRendering(false);
      fetchPdf(0);
    }
  }, [open, buyerId]);

  const fetchPdf = async (isApply) => {
    try {
      setLoading(true);
      setNoDocumentAvailable(false);
      setPdfLoaded(false);
      setPdfLoadError(false);
      setPdfRendering(false);
      setPdfPath(null);

      if (isApply === 1) {
        const signature = getSignatureFromStorage();
        if (!signature) {
          setStep('review');
          setLoading(false);
          return;
        }
      }

      const payload = {
        pdfType:
          userDetail?.primaryUserType === 'Solicitor' ? '66w' : 'acceptance',
        isApply,
        buyerId: buyerId ?? 0,
        propertyId,
      };

      const apiEndpoint =
        userDetail?.primaryUserType === 'Solicitor'
          ? GET_SOLICITOR_PDF
          : GET_VENDOR_PDF;

      const res = await getApiCall(apiEndpoint, payload);

      if (res?.data?.status !== 1 || !res?.data?.fileURL) {
        setNoDocumentAvailable(true);
        setPdfLoadError(true);
        return;
      }

      setPdfPath(res.data.fileURL);
      setPdfRendering(true);
    } catch {
      setNoDocumentAvailable(true);
      setPdfLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentLoad = (e) => {
    setNumPages(e.doc.numPages);
    setPdfRendering(false);
    setPdfLoaded(true);
  };

  const handleDocumentLoadError = (error) => {
    setPdfLoadError(true);
    setPdfRendering(false);
    setPdfLoaded(false);
  };

  const canApplySignature = useMemo(() => {
    const isReady =
      !confirmLoading &&
      !loading &&
      !pdfRendering &&
      pdfLoaded &&
      !pdfLoadError &&
      !noDocumentAvailable &&
      pdfPath !== null;

    return isReady;
  }, [
    confirmLoading,
    loading,
    pdfRendering,
    pdfLoaded,
    pdfLoadError,
    noDocumentAvailable,
    pdfPath,
  ]);

  const handleCancel = () => {
    userDetail?.primaryUserType === 'Solicitor'
      ? getListInfo()
      : getListInfo('apiupdated', propertyId);
    onClose();
  };

  const handleEditSignature = () => {
    setOpenSignPad(true);
  };

  const handleSignatureSave = async (base64Image) => {
    try {
      sessionStorage.setItem(
        'buyerSignature',
        JSON.stringify({
          userId: vendorId,
          base64: base64Image,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (e) {
      console.error('Failed to save to sessionStorage', e);
    }

    try {
      const blob = await (await fetch(base64Image)).blob();
      const file = new File([blob], 'signature.png', { type: 'image/png' });

      const formData = new FormData();
      formData.append('userId', vendorId);
      formData.append(
        'userType',
        userDetail?.primaryUserType === 'Solicitor' ? 'Solicitor' : 'User'
      );
      formData.append('requestFile', file);

      const response = await postApiCall(
        `${API_URL}${UPLOAD_VENDOR_SIGN}`,
        formData,
        true
      );

      const { status, signUrl, message } = response?.data || {};

      if (status === 1) {
        sessionStorage.setItem('signUrl', signUrl);

        const updatedUserDetail = { ...userDetail, signUrl };
        localStorage.setItem('userDetail', JSON.stringify(updatedUserDetail));

        setStep('confirm');
        fetchPdf(1);
      } else {
        console.error('API Error:', message);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setOpenSignPad(false);
    }
  };

  const handleConfirmSignature = async () => {
    setConfirmLoading(true);
    try {
      const payload = {
        propertyId,
        buyerId: buyerId ?? 0,
        offerUniqueId,
        pdfType:
          userDetail?.primaryUserType === 'Solicitor' ? '66w' : 'acceptance',
        ...(userDetail?.primaryUserType === 'Vendor' && { status }),
        pdfPath,
      };

      const endpoint =
        userDetail?.primaryUserType === 'Solicitor'
          ? SOLICITOR_CONFIRM_SIGNATURE
          : VENDOR_CONFIRM_SIGNATURE;

      const res = await postApiCall(endpoint, payload);

      if (!isDialogOpenRef.current) return;

      const apiMessage = res?.data?.message || '';

      let formattedMessage = apiMessage;

      if (
        res?.data?.status === 1 &&
        userDetail?.primaryUserType === 'Solicitor' &&
        payload?.pdfType === '66w'
      ) {
        const match = apiMessage.match(/for the buyer\s+(.*?)\s+for/i);

        if (match && match[1]) {
          const buyerName = match[1];

          formattedMessage = apiMessage.replace(
            buyerName,
            `<span style="font-weight:600;">${buyerName}</span>`
          );
        }
      }

      Swal.fire({
        icon: res?.data?.status === 1 ? 'success' : 'error',
        title: res?.data?.status === 1 ? res?.data?.heading : 'Error',
        html: formattedMessage || '',
        confirmButtonText: 'Close',
      });

      if (res?.data?.status === 1) {
        userDetail?.primaryUserType === 'Solicitor'
          ? await getListInfo()
          : await getListInfo('apiupdated', propertyId);
        history.replace({
          pathname: location.pathname,
          state: {},
        });
      }
    } finally {
      setConfirmLoading(false);
      onClose();
    }
  };

  const handleApplyOrCreateSignature = async () => {
    if (hasSignature) {
      const signature = getSignatureFromStorage();
      if (!signature) {
        setOpenSignPad(true);
        return;
      }
      setStep('confirm');
      fetchPdf(1);
    } else {
      setOpenSignPad(true);
    }
  };

  useEffect(() => {
    if (pdfPath) {
      setPdfRendering(true);
    }
  }, [pdfPath]);

  return (
    <>
      <Dialog
        open={open}
        fullScreen={matches450}
        PaperProps={{
          sx: {
            width: matches450 ? '100%' : '800px',
            maxWidth: 'unset',
            padding: matches450 ? 1 : 1,
            height: matches450
              ? '100vh'
              : userDetail?.primaryUserType === 'Solicitor'
              ? '1180px'
              : '1000px',
          },
        }}
      >
        <DialogTitle>
          <Typography sx={{ ...poppins400, fontSize: 14, color: '#666' }}>
            {address}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box display="flex" gap={1} mb={1}>
            <Pendingwarning />
            <Typography sx={{ ...poppins500, fontSize: 24 }}>
              {step === 'review' ? 'Review Document' : 'Confirm Signature'}
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography sx={{ ...poppins400, fontSize: 16, color: '#666' }}>
              Review the document below and apply your signature to proceed.
            </Typography>
          </Box>

          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              overflowY: numPages > 1 ? 'auto' : 'hidden',
              position: 'relative',
              mt: '10px',
            }}
          >
            {(loading || pdfRendering) && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  zIndex: 5,
                }}
              >
                <img
                  src={Loading}
                  alt="loading"
                  width={40}
                  className="loaderImage"
                />
              </Box>
            )}

            {confirmLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  zIndex: 15,
                }}
              >
                <Box textAlign="center">
                  <img
                    alt="Loader"
                    src={Loading}
                    className="loaderImage"
                    style={{ height: '75px', width: '75px' }}
                  />
                </Box>
              </Box>
            )}

            {!loading && pdfPath && (
              <Box
                sx={{
                  height: '100%',
                  opacity: pdfRendering ? 0 : 1,
                  filter: confirmLoading ? 'blur(4px)' : 'none',
                  transition: 'filter 0.25s ease',
                  pointerEvents: confirmLoading ? 'none' : 'auto',
                }}
              >
                <CommonPdfViewer
                  fileUrl={pdfPath}
                  onDocumentLoad={handleDocumentLoad}
                  onDocumentLoadError={handleDocumentLoadError}
                />
              </Box>
            )}

            {!loading &&
              !pdfRendering &&
              !pdfLoaded &&
              !pdfPath &&
              noDocumentAvailable && (
                <Typography
                  sx={{
                    ...poppins400,
                    fontSize: 16,
                    color: '#666',
                    textAlign: 'center',
                    p: 3,
                  }}
                >
                  No document available
                </Typography>
              )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{ ...poppins400, fontSize: 14, textTransform: 'none' }}
            disabled={confirmLoading}
          >
            Cancel
          </Button>

          {step === 'review' && (
            <Button
              variant="contained"
              sx={{
                ...poppins400,
                fontSize: 14,
                textTransform: 'none',
                opacity: !canApplySignature ? 0.6 : 1,
              }}
              onClick={handleApplyOrCreateSignature}
              disabled={!canApplySignature || confirmLoading}
            >
              Apply Signature
            </Button>
          )}

          {step === 'confirm' && (
            <>
              <Button
                variant="contained"
                sx={{ ...poppins400, fontSize: 14, textTransform: 'none' }}
                onClick={handleEditSignature}
                disabled={confirmLoading || loading || pdfRendering}
              >
                Update Signature
              </Button>
              <Button
                variant="contained"
                sx={{ ...poppins400, fontSize: 14, textTransform: 'none' }}
                onClick={handleConfirmSignature}
                disabled={confirmLoading || loading || pdfRendering}
              >
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <CustomSignaturePadDialog
        open={openSignPad}
        onClose={() => setOpenSignPad(false)}
        onSave={handleSignatureSave}
      />
    </>
  );
};

export default GeneratePdfDialog;
